/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent } from '@alfresco/aca-shared';
import { ContentModelAdminService } from '../document-types/content-model-admin.service';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { DocumentTypesService, SchemaField } from '../document-types/document-types.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/** The kinds of field an administrator can ask for, in the model's own terms. */
interface FieldKind {
  id: string;
  label: string;
  /** `d:*` type written into the model. */
  dataType: string;
  /** Whether the field carries a list of allowed values. */
  list: boolean;
}

const KINDS: FieldKind[] = [
  { id: 'text', label: 'DMS.FIELDS.KIND_TEXT', dataType: 'd:text', list: false },
  { id: 'list', label: 'DMS.FIELDS.KIND_LIST', dataType: 'd:text', list: true },
  { id: 'date', label: 'DMS.FIELDS.KIND_DATE', dataType: 'd:date', list: false },
  { id: 'number', label: 'DMS.FIELDS.KIND_NUMBER', dataType: 'd:int', list: false },
  { id: 'boolean', label: 'DMS.FIELDS.KIND_BOOLEAN', dataType: 'd:boolean', list: false }
];

/**
 * Values the application refers to by name, so taking one out would break a
 * screen rather than just narrow a list: the three status filters on All
 * Documents and the button that saves an upload as a draft, and the two
 * confidentiality levels that raise the lock icon in the document list.
 */
const WIRED_IN_VALUES = new Set(['draft', 'pending', 'approved', 'confidential', 'secret']);

/** The fields the deployed model ships with, which the app itself builds on. */
const BUILT_IN = new Set([
  'dms:documentCode',
  'dms:documentType',
  'dms:documentDate',
  'dms:department',
  'dms:documentOwner',
  'dms:status',
  'dms:confidentiality'
]);

/**
 * The metadata fields every managed document carries (UI-09).
 *
 * Adding, changing and removing them means editing the repository's own content
 * model — {@link ContentModelAdminService} does the writing. Two things are
 * deliberately not offered: a field's name and its data type never change once
 * it exists, because the repository refuses to retype a property that documents
 * already carry and a renamed one would leave their values orphaned.
 *
 * The seven fields the model ships with are shown but not editable here: the
 * upload form, the filters and the expiry feature are all written against them
 * by name, so removing one would break screens rather than just the catalogue.
 */
@Component({
  selector: 'app-metadata-fields',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatProgressBarModule, MatTooltipModule, PageHeaderComponent, PageStateComponent, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './metadata-fields.component.html',
  styleUrl: './metadata-fields.component.scss'
})
export class MetadataFieldsComponent implements OnInit {
  /** Whether the screen sits inside the settings page rather than on its own route. */
  @Input() embedded = false;

  private readonly service = inject(DocumentTypesService);
  private readonly admin = inject(ContentModelAdminService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  readonly fields = signal<SchemaField[]>([]);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly schemaMissing = signal(false);
  /** Whether this account may change the model; null until the answer is in. */
  readonly canEdit = signal<boolean | null>(null);
  readonly saving = signal(false);

  /** The field being written: `''` for a new one, otherwise the QName. */
  readonly editing = signal<string | null>(null);
  /** The row behind that, so the form knows what it may offer. */
  readonly editingField = signal<SchemaField | null>(null);
  readonly kinds = KINDS;

  draftLabel = '';
  draftCode = '';
  draftKind = 'text';
  draftDefault = '';
  /** The allowed values, one per line, which is how they are typed. */
  draftOptions = '';

  ngOnInit(): void {
    this.reload();
    this.admin.canEdit().subscribe((allowed) => this.canEdit.set(allowed));
  }

  reload(): void {
    this.loading.set(true);
    this.failed.set('');
    this.service.catalogue().subscribe({
      next: (catalogue) => {
        this.fields.set(catalogue.fields);
        this.schemaMissing.set(!catalogue.fields.length);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.failed.set(this.translate.instant('DMS.FIELDS.LOAD_FAIL'));
      }
    });
  }

  // ── reading a row ──────────────────────────────────────────────────────────

  builtIn(field: SchemaField): boolean {
    return BUILT_IN.has(field.id);
  }

  /** Whether this field carries a list of allowed values at all. */
  isList(field: SchemaField): boolean {
    return field.options.length > 0;
  }

  kindOf(field: SchemaField): string {
    const kind = field.options.length ? 'list' : (KINDS.find((entry) => entry.dataType === field.dataType)?.id ?? 'text');
    return this.translate.instant(KINDS.find((entry) => entry.id === kind)?.label ?? 'DMS.FIELDS.KIND_TEXT');
  }

  /** The QName as it will be written, shown while the name is being typed. */
  get codePreview(): string {
    const name = ContentModelAdminService.toName(this.draftCode || this.draftLabel);
    return name ? `dms:${name}` : '';
  }

  get kind(): FieldKind {
    return KINDS.find((entry) => entry.id === this.draftKind) ?? KINDS[0];
  }

  get needsOptions(): boolean {
    return this.kind.list;
  }

  /**
   * Whether the form should ask for the starting values.
   *
   * Only when a list field is being created: once it exists its values are
   * managed one at a time, where each one's usage count is known and a value
   * cannot be dropped by retyping the list around it.
   */
  get needsStartingValues(): boolean {
    return this.kind.list && !this.editing();
  }

  private get options(): string[] {
    return this.draftOptions
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }

  // ── the values one list field allows ─────────────────────────────

  /** The field whose values are open for managing, if any. */
  readonly managing = signal<SchemaField | null>(null);
  /** How many documents carry each of them, once the counts come back. */
  readonly usage = signal<Record<string, { value: number; exact: boolean }>>({});
  readonly usageLoading = signal(false);
  /** The value being renamed, if any. */
  readonly renaming = signal<string | null>(null);

  newValueLabel = '';
  newValueCode = '';
  renameLabel = '';

  manageValues(field: SchemaField): void {
    if (this.managing()?.id === field.id) {
      this.managing.set(null);
      return;
    }
    this.cancelEdit();
    this.managing.set(field);
    this.renaming.set(null);
    this.newValueLabel = '';
    this.newValueCode = '';
    this.countUsage(field);
  }

  private countUsage(field: SchemaField): void {
    this.usage.set({});
    this.usageLoading.set(true);
    this.service
      .valueUsage(
        field.id,
        field.options.map((option) => option.value)
      )
      .subscribe({
        next: (counts) => {
          this.usage.set(counts);
          this.usageLoading.set(false);
        },
        error: () => this.usageLoading.set(false)
      });
  }

  closeValues(): void {
    this.managing.set(null);
    this.renaming.set(null);
  }

  countOf(value: string): number | null {
    return this.usage()[value]?.value ?? null;
  }

  /** Whether the application would notice this value disappearing. */
  wiredIn(value: string): boolean {
    return WIRED_IN_VALUES.has(value);
  }

  canRemoveValue(value: string): boolean {
    if (!this.canEdit() || this.saving() || this.usageLoading() || this.wiredIn(value)) {
      return false;
    }
    const count = this.usage()[value];
    return !!count && count.value === 0 && count.exact;
  }

  removeReason(value: string): string {
    if (this.wiredIn(value)) {
      return 'DMS.FIELDS.VALUE_WIRED_IN';
    }
    return this.canRemoveValue(value) ? 'DMS.FIELDS.VALUE_DELETE' : 'DMS.FIELDS.VALUE_IN_USE';
  }

  /** The code as it will be written, shown while the name is being typed. */
  get newValueCodePreview(): string {
    return ContentModelAdminService.toCode(this.newValueCode || this.newValueLabel);
  }

  canAddValue(): boolean {
    const field = this.managing();
    if (!field || this.saving() || !this.newValueLabel.trim() || !this.newValueCodePreview) {
      return false;
    }
    return !field.options.some((option) => option.value === this.newValueCodePreview);
  }

  addValue(): void {
    const field = this.managing();
    if (!field || !this.canAddValue()) {
      return;
    }
    this.saving.set(true);
    this.admin.addValue(field.id, this.newValueCodePreview, this.newValueLabel).subscribe({
      next: () => {
        this.saving.set(false);
        this.notification.showInfo(this.translate.instant('DMS.FIELDS.VALUE_ADDED'));
        this.reloadKeepingOpen(field.id);
      },
      error: (error) => {
        this.saving.set(false);
        const duplicate = String(error?.message ?? '').includes('duplicate');
        this.notification.showError(this.translate.instant(duplicate ? 'DMS.FIELDS.VALUE_DUPLICATE' : 'DMS.FIELDS.SAVE_FAIL'));
      }
    });
  }

  startRenameValue(option: { value: string; label: string }): void {
    this.renaming.set(option.value);
    this.renameLabel = this.translate.instant(option.label);
  }

  cancelRenameValue(): void {
    this.renaming.set(null);
  }

  saveValueName(value: string): void {
    const field = this.managing();
    if (!field || !this.renameLabel.trim()) {
      this.renaming.set(null);
      return;
    }
    this.saving.set(true);
    this.admin.renameValue(value, this.renameLabel).subscribe({
      next: () => {
        this.saving.set(false);
        this.renaming.set(null);
        this.notification.showInfo(this.translate.instant('DMS.FIELDS.VALUE_RENAMED'));
        this.reloadKeepingOpen(field.id);
      },
      error: () => {
        this.saving.set(false);
        this.notification.showError(this.translate.instant('DMS.FIELDS.SAVE_FAIL'));
      }
    });
  }

  removeValue(option: { value: string; label: string }): void {
    const field = this.managing();
    if (!field || !this.canRemoveValue(option.value)) {
      return;
    }
    if (!confirm(this.translate.instant('DMS.FIELDS.VALUE_DELETE_CONFIRM', { p0: this.translate.instant(option.label) }))) {
      return;
    }
    this.saving.set(true);
    this.admin.removeValue(field.id, option.value).subscribe({
      next: () => {
        this.saving.set(false);
        this.notification.showInfo(this.translate.instant('DMS.FIELDS.VALUE_DELETED'));
        this.reloadKeepingOpen(field.id);
      },
      error: () => {
        this.saving.set(false);
        this.notification.showError(this.translate.instant('DMS.FIELDS.SAVE_FAIL'));
      }
    });
  }

  /** Reloads the catalogue and reopens the field that was being managed. */
  private reloadKeepingOpen(fieldId: string): void {
    this.newValueLabel = '';
    this.newValueCode = '';
    this.service.catalogue().subscribe((catalogue) => {
      this.fields.set(catalogue.fields);
      const field = catalogue.fields.find((entry) => entry.id === fieldId);
      if (field) {
        this.managing.set(field);
        this.countUsage(field);
      }
    });
  }

  // ── writing one ────────────────────────────────────────────────────────────

  startAdd(): void {
    this.editing.set('');
    this.editingField.set(null);
    this.draftLabel = '';
    this.draftCode = '';
    this.draftKind = 'text';
    this.draftDefault = '';
    this.draftOptions = '';
  }

  startEdit(field: SchemaField): void {
    this.editing.set(field.id);
    this.editingField.set(field);
    this.draftLabel = this.translate.instant(field.label);
    this.draftCode = field.id;
    this.draftKind = field.options.length ? 'list' : (KINDS.find((entry) => entry.dataType === field.dataType)?.id ?? 'text');
    this.draftDefault = field.defaultValue;
    // the codes, which is what the model stores and what a document carries —
    // never the labels, which are only how those codes read
    this.draftOptions = field.options.map((option) => option.value).join('\n');
  }

  cancelEdit(): void {
    this.editing.set(null);
    this.editingField.set(null);
  }

  /** The values as they will read once saved, so the codes are not typed blind. */
  get optionPreview(): { value: string; label: string }[] {
    return this.options.map((value) => ({ value, label: this.translate.instant(DmsMetadataService.label(value)) }));
  }

  canSaveDraft(): boolean {
    if (this.saving() || !this.draftLabel.trim()) {
      return false;
    }
    if (this.needsStartingValues && !this.options.length) {
      return false;
    }
    if (this.editing()) {
      return true;
    }
    return !!this.codePreview && !this.fields().some((field) => field.id === this.codePreview);
  }

  saveDraft(): void {
    if (!this.canSaveDraft()) {
      return;
    }
    const existing = this.editing();
    this.saving.set(true);

    // an existing list keeps exactly the values it has; only its name and
    // default move here, because the values have a manager of their own
    const values = existing ? (this.editingField()?.options ?? []).map((option) => option.value) : this.options;
    const write = existing
      ? this.admin.updateField(existing, this.draftLabel, this.draftDefault, values)
      : this.admin.addField(this.codePreview, this.draftLabel, this.kind.dataType, this.draftDefault, values);

    write.subscribe({
      next: () => {
        this.saving.set(false);
        this.cancelEdit();
        this.notification.showInfo(this.translate.instant(existing ? 'DMS.FIELDS.SAVED' : 'DMS.FIELDS.ADDED'));
        this.reload();
      },
      error: (error) => {
        this.saving.set(false);
        const duplicate = String(error?.message ?? '').includes('duplicate');
        this.notification.showError(this.translate.instant(duplicate ? 'DMS.FIELDS.DUPLICATE' : 'DMS.FIELDS.SAVE_FAIL'));
      }
    });
  }

  remove(field: SchemaField): void {
    if (!this.canEdit() || this.builtIn(field) || this.saving()) {
      return;
    }
    if (!confirm(this.translate.instant('DMS.FIELDS.DELETE_CONFIRM', { p0: this.translate.instant(field.label) }))) {
      return;
    }
    this.saving.set(true);
    this.admin.removeField(field.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.notification.showInfo(this.translate.instant('DMS.FIELDS.DELETED'));
        this.reload();
      },
      error: (error) => {
        this.saving.set(false);
        // the repository refuses while any document still carries a value
        const inUse = JSON.stringify(error ?? '').includes('is in use');
        this.notification.showError(this.translate.instant(inUse ? 'DMS.FIELDS.DELETE_IN_USE' : 'DMS.FIELDS.DELETE_FAIL'));
      }
    });
  }

  trackById(_index: number, field: SchemaField): string {
    return field.id;
  }
}
